import { type ReactNode, useEffect, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { sentinel } from '../theme/semantic.stylex';

export interface OpsShellNavItem {
  key: string;
  label: ReactNode;
  href?: string;
  icon?: ReactNode;
  active?: boolean;
  onClick?: () => void;
  trailing?: ReactNode;
}

export interface OpsShellBadge {
  label: ReactNode;
  tone?: 'brand' | 'safe' | 'warn' | 'danger' | 'info';
}

export interface OpsShellProps {
  productName: ReactNode;
  productMode?: OpsShellBadge;
  nav: ReadonlyArray<OpsShellNavItem>;
  topbar?: ReactNode;
  userMenu?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  brandLogo?: ReactNode;
  navItemTestId?: (key: string) => string;
  /** When true, the sidenav can be collapsed to icon-only width. */
  collapsible?: boolean;
  /** LocalStorage key used to persist collapsed state. If omitted, state is in-memory only. */
  collapsedStorageKey?: string;
  /** Accessible labels for the collapse toggle (i18n). */
  collapseLabel?: string;
  expandLabel?: string;
}

const RAIL_EXPANDED = '232px';
const RAIL_COLLAPSED = '56px';

const styles = stylex.create({
  root: {
    minHeight: '100vh',
    backgroundColor: sentinel.surfaceApp,
    color: sentinel.textBody,
    fontFamily: sentinel.fontBody,
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
  },
  topbar: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'center',
    gap: '1rem',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    backgroundColor: sentinel.surfaceApp,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: sentinel.borderDefault,
    minHeight: '3.5rem',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  brand: { display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 },
  brandLogo: { display: 'grid', placeItems: 'center', flexShrink: 0 },
  brandName: {
    fontFamily: sentinel.fontDisplay,
    fontSize: '0.9375rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: sentinel.textBody,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  modeBadge: {
    fontFamily: sentinel.fontBody,
    fontSize: '0.6875rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontWeight: 600,
    paddingTop: '0.125rem',
    paddingBottom: '0.125rem',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    borderRadius: '999px',
    width: 'fit-content',
    flexShrink: 0,
  },
  topbarCenter: { display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 },
  topbarRight: { display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'flex-end' },

  body: {
    display: 'grid',
    minHeight: 0,
  },
  bodyExpanded: { gridTemplateColumns: `${RAIL_EXPANDED} minmax(0, 1fr)` },
  bodyCollapsed: { gridTemplateColumns: `${RAIL_COLLAPSED} minmax(0, 1fr)` },

  rail: {
    position: 'sticky',
    top: '3.5rem',
    height: 'calc(100vh - 3.5rem)',
    display: 'grid',
    gridTemplateRows: '1fr auto',
    backgroundColor: sentinel.surfaceSunken,
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: sentinel.borderDefault,
    overflow: 'hidden',
    transitionProperty: 'width',
    transitionDuration: sentinel.motionBase,
    transitionTimingFunction: sentinel.easeOut,
  },
  navList: {
    listStyle: 'none',
    margin: 0,
    padding: '0.75rem 0.5rem',
    display: 'grid',
    gridAutoRows: 'min-content',
    alignContent: 'start',
    gap: '0.125rem',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  navItem: { position: 'relative' },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    paddingLeft: '0.875rem',
    paddingRight: '0.875rem',
    borderRadius: '0.5rem',
    color: sentinel.textMuted,
    fontFamily: sentinel.fontBody,
    fontSize: '0.875rem',
    fontWeight: 500,
    textDecoration: 'none',
    cursor: 'pointer',
    transitionProperty: 'background-color, color',
    transitionDuration: sentinel.motionFast,
    transitionTimingFunction: sentinel.easeOut,
    backgroundColor: { default: 'transparent', ':hover': sentinel.stateHover },
    backgroundImage: 'none',
    borderWidth: 0,
    width: '100%',
    textAlign: 'left',
  },
  navLinkCollapsed: {
    paddingLeft: 0,
    paddingRight: 0,
    justifyContent: 'center',
    gap: 0,
  },
  navLinkActive: { color: sentinel.textBody, backgroundColor: sentinel.stateSelect },
  navAccent: {
    position: 'absolute',
    top: '0.375rem',
    bottom: '0.375rem',
    left: 0,
    width: '2px',
    borderRadius: '2px',
    backgroundColor: sentinel.brandPrimary,
  },
  navLabel: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  navLabelHidden: { display: 'none' },
  navIcon: {
    display: 'grid',
    placeItems: 'center',
    width: '1.125rem',
    height: '1.125rem',
    flexShrink: 0,
    color: 'currentColor',
  },
  navFallbackGlyph: {
    fontFamily: sentinel.fontDisplay,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'currentColor',
  },
  navTrailing: { fontSize: '0.75rem', color: sentinel.textSubtle },
  navTrailingHidden: { display: 'none' },

  railFoot: {
    display: 'grid',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: sentinel.borderDefault,
  },
  railFooter: {
    padding: '0.875rem 1rem',
    fontSize: '0.8125rem',
    color: sentinel.textMuted,
  },
  railFooterHidden: { display: 'none' },
  toggleButton: {
    appearance: 'none',
    border: 0,
    background: 'transparent',
    color: { default: sentinel.textMuted, ':hover': sentinel.textBody },
    cursor: 'pointer',
    padding: '0.625rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    fontFamily: sentinel.fontBody,
    fontSize: '0.75rem',
    letterSpacing: '0.04em',
    transitionProperty: 'color, background-color',
    transitionDuration: sentinel.motionFast,
    transitionTimingFunction: sentinel.easeOut,
    backgroundColor: { default: 'transparent', ':hover': sentinel.stateHover },
  },
  toggleLabelHidden: { display: 'none' },

  main: { display: 'grid', gridTemplateRows: '1fr', minHeight: 0 },
  content: { display: 'grid', alignContent: 'start' },
});

const modeBadgeTones = stylex.create({
  brand: { backgroundColor: sentinel.brandPrimary, color: sentinel.textOnAccent },
  safe: { backgroundColor: sentinel.riskSafe, color: sentinel.textOnAccent },
  warn: { backgroundColor: sentinel.riskMedium, color: sentinel.textOnAccent },
  danger: { backgroundColor: sentinel.riskHigh, color: sentinel.textOnAccent },
  info: { backgroundColor: sentinel.riskInfo, color: sentinel.textOnAccent },
});

function readCollapsed(key: string | undefined): boolean {
  if (!key) return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(key: string | undefined, value: boolean): void {
  if (!key) return;
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // Storage may be unavailable (private mode, SSR) — ignore.
  }
}

function firstChar(label: ReactNode): string {
  if (typeof label === 'string') return label.trim().charAt(0).toUpperCase();
  if (typeof label === 'number') return String(label).charAt(0);
  return '·';
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {direction === 'left' ? (
        <polyline points="9 3 4 7 9 11" />
      ) : (
        <polyline points="5 3 10 7 5 11" />
      )}
    </svg>
  );
}

export function OpsShell({
  productName,
  productMode,
  nav,
  topbar,
  userMenu,
  footer,
  children,
  brandLogo,
  navItemTestId,
  collapsible = true,
  collapsedStorageKey,
  collapseLabel = 'Collapse sidebar',
  expandLabel = 'Expand sidebar',
}: OpsShellProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed(collapsedStorageKey));

  useEffect(() => {
    writeCollapsed(collapsedStorageKey, collapsed);
  }, [collapsedStorageKey, collapsed]);

  const railLabel = collapsed ? expandLabel : collapseLabel;

  return (
    <div {...stylex.props(styles.root)}>
      <header {...stylex.props(styles.topbar)}>
        <div {...stylex.props(styles.brand)}>
          {brandLogo ? <span {...stylex.props(styles.brandLogo)}>{brandLogo}</span> : null}
          <span {...stylex.props(styles.brandName)}>{productName}</span>
          {productMode ? (
            <span {...stylex.props(styles.modeBadge, modeBadgeTones[productMode.tone ?? 'brand'])}>
              {productMode.label}
            </span>
          ) : null}
        </div>
        <div {...stylex.props(styles.topbarCenter)}>{topbar}</div>
        <div {...stylex.props(styles.topbarRight)}>{userMenu}</div>
      </header>

      <div {...stylex.props(styles.body, collapsed ? styles.bodyCollapsed : styles.bodyExpanded)}>
        <aside {...stylex.props(styles.rail)} aria-label="Primary navigation">
          <ul {...stylex.props(styles.navList)}>
            {nav.map((item) => {
              const linkProps = {
                ...stylex.props(
                  styles.navLink,
                  collapsed && styles.navLinkCollapsed,
                  item.active && styles.navLinkActive,
                ),
                onClick: item.onClick,
                title: collapsed && typeof item.label === 'string' ? item.label : undefined,
                'aria-label':
                  collapsed && typeof item.label === 'string' ? item.label : undefined,
              };
              const iconNode = item.icon ?? (
                <span {...stylex.props(styles.navFallbackGlyph)} aria-hidden="true">
                  {firstChar(item.label)}
                </span>
              );
              return (
                <li
                  key={item.key}
                  {...stylex.props(styles.navItem)}
                  data-testid={navItemTestId?.(item.key)}
                >
                  {item.active ? (
                    <span {...stylex.props(styles.navAccent)} aria-hidden="true" />
                  ) : null}
                  {item.href ? (
                    <a href={item.href} {...linkProps}>
                      <span {...stylex.props(styles.navIcon)}>{iconNode}</span>
                      <span {...stylex.props(styles.navLabel, collapsed && styles.navLabelHidden)}>
                        {item.label}
                      </span>
                      {item.trailing ? (
                        <span
                          {...stylex.props(
                            styles.navTrailing,
                            collapsed && styles.navTrailingHidden,
                          )}
                        >
                          {item.trailing}
                        </span>
                      ) : null}
                    </a>
                  ) : (
                    <button type="button" {...linkProps}>
                      <span {...stylex.props(styles.navIcon)}>{iconNode}</span>
                      <span {...stylex.props(styles.navLabel, collapsed && styles.navLabelHidden)}>
                        {item.label}
                      </span>
                      {item.trailing ? (
                        <span
                          {...stylex.props(
                            styles.navTrailing,
                            collapsed && styles.navTrailingHidden,
                          )}
                        >
                          {item.trailing}
                        </span>
                      ) : null}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          <div {...stylex.props(styles.railFoot)}>
            {footer ? (
              <div {...stylex.props(styles.railFooter, collapsed && styles.railFooterHidden)}>
                {footer}
              </div>
            ) : null}
            {collapsible ? (
              <button
                type="button"
                {...stylex.props(styles.toggleButton)}
                onClick={() => setCollapsed((c) => !c)}
                aria-expanded={!collapsed}
                aria-label={railLabel}
                data-testid="ops-shell-toggle"
              >
                <ChevronIcon direction={collapsed ? 'right' : 'left'} />
                <span {...stylex.props(collapsed && styles.toggleLabelHidden)}>{railLabel}</span>
              </button>
            ) : null}
          </div>
        </aside>
        <main {...stylex.props(styles.main)}>
          <div {...stylex.props(styles.content)}>{children}</div>
        </main>
      </div>
    </div>
  );
}
